import { PurgeLevel } from "./constants";

/*
 * Column definitions for the Preview grid — the visible column set of the legacy where-builder grid, one
 * set per purge level.
 *
 * Which columns those are is decided by standard `TField.Visible` here, NOT by the Abecas
 * `VisibleOnGrid` property: WMD0120's three datasets are TAbADOQuery, and TAbWhereBuilder.Preview's ADO
 * path builds both its FieldList and its column set from `Fields[i].Visible`
 * (uAbWhereBuilder.pas:1379-1398). The TAbQuery path a few lines above it is the one that reads
 * VisibleOnGrid — a trap, since every field in these datasets has VisibleOnGrid = True and only three
 * to five have Visible = True.
 *
 * So the legacy sets are exactly: Item = Customer, Item, Description - 1; Lot = those plus Lot; Sublot =
 * those plus Sublot. Record # is our addition (Architecture.md rule 10) and is lifted to the front, as
 * every migrated grid does.
 *
 * dataField casing matches NeverUsedRecordDTO's property names exactly, because the endpoint serialises
 * with DefaultContractResolver, i.e. PascalCase straight off the DTO.
 *
 * dataType is declared on EVERY column, deliberately. DevExtreme only infers it from the first non-null
 * value, and Customer is routinely empty for a whole result set (the IDMASTER join is LEFT OUTER, and a
 * never-used item may well have no customer). With nothing to infer from, the column gets no filter-row
 * editor and the user silently loses the ability to filter it.
 *
 * minWidth on every column keeps `columnAutoWidth` from squeezing the columns: once the total exceeds
 * the container DevExtreme shows a horizontal scrollbar instead of compressing everything to slivers.
 *
 * The three arrays are module-level and frozen because OperationResultGrid is memo-wrapped: a fresh
 * array per render would remount GridLayoutManager and re-fire GetGridLayouts on every keystroke.
 */

const TEXT = "string";

// Widths tuned to the caption plus a plausible value, so nothing truncates at the default zoom.
const W_SHORT = 90;
const W_CODE = 140;
const W_NAME = 260;

export const PREVIEW_KEY_EXPR = "RowId";

const text = (dataField, caption, width = W_CODE) => ({
    dataField,
    caption,
    dataType: TEXT,
    minWidth: width,
});

/*
 * The key. Hidden and kept out of the column chooser (Architecture.md rule 8) — users never see raw ids,
 * and AI8 did not show it either: ROWID is not a persistent field in any of the three datasets.
 */
const rowIdColumn = { dataField: "RowId", dataType: TEXT, visible: false, showInColumnChooser: false };

// Captions verbatim from the DFM DisplayLabels, including the spaced-hyphen "Description - 1".
const recordNumber = text("RecordNumber", "Record #", W_SHORT);
const identityId = text("IdentityId", "Customer");
const itemCode = text("ItemCode", "Item");
const lotCode = text("LotCode", "Lot");
const sublotCode = text("SublotCode", "Sublot");
const description1 = text("Description1", "Description - 1", W_NAME);

const ITEM_COLUMNS = Object.freeze([rowIdColumn, recordNumber, identityId, itemCode, description1]);

const LOT_COLUMNS = Object.freeze([rowIdColumn, recordNumber, identityId, itemCode, lotCode, description1]);

const SUBLOT_COLUMNS = Object.freeze([
    rowIdColumn,
    recordNumber,
    identityId,
    itemCode,
    lotCode,
    sublotCode,
    description1,
]);

/**
 * The column set for a purge level. Returns one of three stable array instances — never a new array, so
 * the memoised grid is not remounted on every render.
 */
export const previewColumns = (purgeLevel) => {
    if (purgeLevel === PurgeLevel.Item) {
        return ITEM_COLUMNS;
    }

    return purgeLevel === PurgeLevel.Lot ? LOT_COLUMNS : SUBLOT_COLUMNS;
};
